import React from "react";
import axios from "axios";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import { browserHistory, Link } from "react-router";
import { NIBHV_NUMBER_REGEX, useCurrentUser, userMayLogin } from "../../utils/auth";

yup.setLocale({
    mixed: {
        required: "Dit veld is verplicht"
    }
});

const schema = yup.object({
    username: yup
        .string()
        .required()
        .test("username", "Gebruik je e-mailadres of NIBHV nummer.", (value) => {
            return yup.string().matches(NIBHV_NUMBER_REGEX).isValidSync(value) || yup.string().email().isValidSync(value);
        }),
    password: yup.string().required(),
    token: yup.string().length(6, "Dit is geen geldig token")
});

const credentialsSchema = schema.omit(["token"]);

// error messages when authorization is not possible:
const errorMessages = {
    login_failed: "Inloggen mislukt. Neem contact op met NIBHV: 010-2892888",
    invalid_role: "U heeft geen toegang tot deze applicatie.",
    blocked: "Oeps! Je account lijkt geblokkeerd. Neem contact op met NIBHV: 010-2892888",
    unknown_error: "Oeps! Er is een technische fout opgetreden. Neem contact op met NIBHV als het probleem zich blijft voordoen: 010-2892888"
};

/**
 * Shows the login form.
 * @param {Function} setUserData
 * @param {Function} setAuthToken
 * @param {*} router
 * @returns {JSX.Element}
 */
export default function Login({ setUserData, setAuthToken, router }) {
    const user = useCurrentUser();
    const [step, setStep] = React.useState("credentials");
    const [errorMessage, setErrorMessage] = React.useState(null);
    const {
        register,
        handleSubmit,
        setError,
        setFocus,
        formState: { errors }
    } = useForm({
        resolver: yupResolver(step === "credentials" ? credentialsSchema : schema)
    });

    React.useEffect(() => {
        if (location.pathname === "/") {
            if (user) {
                return router.push("/courses");
            }
            return router.push("/login");
        }
    }, [location.pathname, user]);

    const onSubmit = ({ username, password, token }) => {
        const data = { username, password };
        setErrorMessage(null);

        if (step === "token") {
            Object.assign(data, { token });
        }

        axios
            .post("/v1/auth/", data)
            .then(({ data, status }) => {
                // console.info({status, data});
                switch (status) {
                case 401:
                    if (data?.code === "token_invalid") {
                        setError(
                            "token",
                            {
                                type: "custom",
                                message: "Dit token is niet geldig. Controleer de code in de app en probeer het opnieuw."
                            },
                            { shouldFocus: true }
                        );
                        return;
                    } else {
                        if (data["non_field_errors"]) {
                            setErrorMessage(data["non_field_errors"]);
                            setFocus("username");
                        }
                        // any field specific field errors are put with the field:
                        ["username", "password"].forEach((field, index) => {
                            if (data[field]) {
                                setError(
                                    field,
                                    {
                                        type: "custom",
                                        message: data[field]
                                    },
                                    { shouldFocus: index === 0 }
                                );
                            }
                        });
                    }
                    break;
                case 200:
                    if (data?.code === "token_required") {
                        // Server requires us to enter a token
                        setStep("token");
                        setFocus("token");
                        return;
                    }

                    if (!data.user) {
                        // Malformed response, this should never happen, but let's catch it.
                        setErrorMessage(errorMessages.login_failed);
                        setStep("credentials");
                        return;
                    }

                    // We're logged in.
                    // Check if the user is allowed to use this application.

                    const [mayLogin, errorCode] = userMayLogin(data.user);
                    if (!mayLogin) {
                        setErrorMessage(errorMessages[errorCode]);
                        setStep("credentials");
                        return;
                    }

                    // All good
                    // Stores our user data.
                    setUserData(data.user);
                    setAuthToken(data.token);
                    browserHistory.push("/courses");
                    return;
                default:
                    setErrorMessage(errorMessages.unknown_error);
                    console.warn(`Authentication failed! Status ${status}`, data);
                }
            })
            .catch((error) => {
                setErrorMessage(errorMessages.unknown_error);
                console.warn("Authentication failed!", error);
            });
    };

    return (
        <div className="row justify-content-md-center">
            <div className="form form-signin col-6">
                <div className="section-top">
                    <h1>Login</h1>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className={Object.keys(errors).length && "was-validated"}>
                    <div hidden={step === "token"}>
                        <Field errors={errors} label="E-mailadres of NIBHV nummer" required autoFocus={step === "credentials"} {...register("username")} />
                        <Field errors={errors} label="Wachtwoord" type="password" required {...register("password")} />
                    </div>

                    <div hidden={step === "credentials"}>
                        <Field errors={errors} label="Token" type="token" required={step === "token"} autoFocus={step === "token"} autoComplete="one-time-code" inputMode="numeric" pattern="[0-9]*" {...register("token")} />
                    </div>

                    {errorMessage && (
                        <div className="alert alert-danger" role="alert">
                            {errorMessage}
                        </div>
                    )}

                    <FormGroup>
                        <button className="btn btn-primary" type="submit">
                            Inloggen
                        </button>
                        {step === "credentials" && (
                            <Link className="btn btn-light" to="/password-lost">
                                Wachtwoord vergeten?
                            </Link>
                        )}
                    </FormGroup>

                    <FormGroup>
                        <a className="btn btn-light" target="_blank" href="https://praktijk.nibhv.nl">
                            <i className="fa fa-angle-right" /> Klik hier om naar NIBHV Praktijk voor instructeurs te gaan
                        </a>
                    </FormGroup>
                </form>
            </div>
        </div>
    );
}

const FormGroup = (props) => <div className="form-group" {...props} />;

const Field = React.forwardRef(({ label, name, errors, type = "text", ...props }, ref) => {
    const error = errors?.[name];
    const id = props.id || `${name}-field`;

    const className = "form-control form-control-md" + (error ? " is-invalid" : "");

    return (
        <FormGroup>
            <label htmlFor={id}>{label}</label>
            <input id={id} name={name} type={type} className={className} ref={ref} {...props} />
            {error && <div className="invalid-feedback">{error.message}</div>}
        </FormGroup>
    );
});
